import { useEffect, useState } from "react";
import { authedFetch, restGet } from "../lib/restClient.js";

import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { AgentIcon } from "./Dimension.jsx";

const PROVIDERS = [
    ["openai", "ChatGPT", "chatgpt.com"],
    ["anthropic", "Claude", "claude.ai"],
    ["microsoft", "Microsoft Copilot", "copilot.microsoft.com"],
    ["perplexity", "Perplexity", "perplexity.ai"],
    ["deepseek", "DeepSeek", "chat.deepseek.com"],
    ["google", "Gemini", "gemini.google.com"],
];
const PATH = "/v1/tenant/browser-capture-policy";

export default function BrowserCaptureSettings() {
    const caps = useMyPermissions();
    const canWrite = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    const [policy, setPolicy] = useState(null);
    const [disabled, setDisabled] = useState([]);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);

    function accept(value) {
        setPolicy(value);
        setDisabled(value.disabledProviderTags ?? value.disabled_provider_tags ?? []);
    }

    async function load() {
        setBusy(true);
        setError("");
        try { accept(await restGet(PATH)); }
        catch { setError("Could not load browser capture settings. Try again."); }
        finally { setBusy(false); }
    }

    useEffect(() => { void load(); }, []);

    async function save() {
        setBusy(true);
        setError("");
        setSaved(false);
        try {
            const response = await authedFetch(PATH, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ expected_revision: policy.revision ?? "0", disabled_provider_tags: disabled }),
            });
            if (response.status === 409) {
                accept(await restGet(PATH));
                setError("Another administrator changed these settings. Review the updated selection and save again.");
                return;
            }
            if (!response.ok) throw new Error("save_failed");
            accept(await response.json());
            setSaved(true);
        } catch { setError("Could not save browser capture settings. Try again."); }
        finally { setBusy(false); }
    }

    return (
        <section id="browser-capture" aria-busy={busy} className="grid gap-[16px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]">
            <header className="grid gap-[2px]">
                <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">Browser chat capture</h2>
                <p className="mt-[4px] text-[12px] leading-[1.4] text-[color:var(--muted-soft)]">
                    Choose which browser chats Origin captures from the browser extension across your organization.
                </p>
            </header>
            {!policy && busy ? <p>Loading settings...</p> : null}
            {policy ? (
                <fieldset disabled={busy || !canWrite} className="group grid min-w-0 gap-[12px]">
                    <legend className="sr-only">Browser providers</legend>
                    {PROVIDERS.map(([tag, label, url]) => (
                        <label key={tag} className="flex items-center gap-[8px] text-[12px] leading-[1.3] text-[color:var(--ink)] cursor-pointer group-disabled:cursor-default group-disabled:opacity-50">
                            <input type="checkbox" aria-label={`Capture ${label} browser chats`} className="m-0 h-[13px] w-[13px] shrink-0 cursor-pointer accent-[color:var(--green)] disabled:cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--green)]" checked={!disabled.includes(tag)} onChange={(event) => {
                                setSaved(false);
                                setDisabled((current) => event.target.checked ? current.filter((item) => item !== tag) : [...current, tag]);
                            }} />
                            <AgentIcon name={label} className="h-[14px] w-[14px] shrink-0" />
                            <span className="min-w-0">{label} <span className="break-words text-[color:var(--muted-soft)]">({url})</span></span>
                        </label>
                    ))}
                    <button type="button" onClick={save} className="mt-[4px] h-[30px] justify-self-end rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 disabled:cursor-default cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--green)]">{busy ? "Saving..." : "Save"}</button>
                </fieldset>
            ) : !busy ? <button type="button" onClick={load}>Retry</button> : null}
            {error ? <p role="alert" className="text-xs text-red-400">{error}</p> : null}
            {saved ? <p role="status" className="text-xs">Saved. Changes reach compatible, connected endpoints gradually. Offline endpoints update when they reconnect.</p> : null}
        </section>
    );
}
